import Iconify from '@/components/iconify/iconify';
import { FileText, Package, Printer, Truck } from 'lucide-react';
import React from 'react';

export const DEFAULT_STATES = [
  'DRAFT',
  'CONFIRMED',
  'COD',
  'PARTIALLY_PAID',
  'PAID',
  'READY_FOR_DELIVERY',
  'DELIVERED',
  'RETURNED',
  'REFUNDED',
  'PRINT',
] as const;

export type DefaultState = (typeof DEFAULT_STATES)[number];

export const STATUS_META: Record<string, { title: string; desc: string; icon: React.ReactNode }> = {
  DRAFT: {
    title: 'Draft',
    desc: 'Order created as a draft.',
    icon: <FileText className="h-3.5 w-3.5" />,
  },
  CONFIRMED: {
    title: 'Confirmed',
    desc: 'Order has been confirmed.',
    icon: <Iconify icon="mdi:check-circle" className="h-3.5 w-3.5" />,
  },
  COD: {
    title: 'Cash on Delivery',
    desc: 'Payment will be collected on delivery.',
    icon: <Iconify icon="mdi:cash" className="h-3.5 w-3.5" />,
  },
  PARTIALLY_PAID: {
    title: 'Partially Paid',
    desc: 'Customer has paid part of the amount.',
    icon: <Iconify icon="mdi:cash-clock" className="h-3.5 w-3.5" />,
  },
  PAID: {
    title: 'Paid',
    desc: 'Order is fully paid.',
    icon: <Iconify icon="mdi:cash-check" className="h-3.5 w-3.5" />,
  },
  READY_FOR_DELIVERY: {
    title: 'Ready for Delivery',
    desc: 'Packed and ready to go out.',
    icon: <Package className="h-3.5 w-3.5" />,
  },
  DELIVERED: {
    title: 'Delivered',
    desc: 'Delivered to the customer.',
    icon: <Truck className="h-3.5 w-3.5" />,
  },
  RETURNED: {
    title: 'Returned',
    desc: 'Items were returned.',
    icon: <Iconify icon="mdi:package-down" className="h-3.5 w-3.5" />,
  },
  REFUNDED: {
    title: 'Refunded',
    desc: 'Payment refunded to the customer.',
    icon: <Iconify icon="mdi:cash-refund" className="h-3.5 w-3.5" />,
  },
  PRINT: {
    title: 'Printed',
    desc: 'Invoice or receipt was printed.',
    icon: <Printer className="h-3.5 w-3.5" />,
  },
  ADD_ITEM: {
    title: 'Add Item',
    desc: 'Items were added to the order.',
    icon: <Iconify icon="mdi:package-variant-plus" className="h-3.5 w-3.5" />,
  },
};

export type StatusColors = {
  dot: string;
  badgeBg: string;
  badgeText: string;
  chipActive: string;
  chipSeen: string;
};

export const STATUS_COLORS: Record<string, StatusColors> = {
  DRAFT: {
    dot: 'bg-slate-400',
    badgeBg: 'bg-slate-100',
    badgeText: 'text-slate-600',
    chipActive: 'border-slate-300 bg-slate-100 text-slate-700',
    chipSeen: 'border-slate-200 bg-slate-50 text-slate-600',
  },
  CONFIRMED: {
    dot: 'bg-blue-500',
    badgeBg: 'bg-blue-100',
    badgeText: 'text-blue-600',
    chipActive: 'border-blue-300 bg-blue-100 text-blue-700',
    chipSeen: 'border-blue-200 bg-blue-50 text-blue-600',
  },
  COD: {
    dot: 'bg-amber-400',
    badgeBg: 'bg-amber-100',
    badgeText: 'text-amber-600',
    chipActive: 'border-amber-300 bg-amber-100 text-amber-700',
    chipSeen: 'border-amber-200 bg-amber-50 text-amber-600',
  },
  PARTIALLY_PAID: {
    dot: 'bg-orange-400',
    badgeBg: 'bg-orange-100',
    badgeText: 'text-orange-600',
    chipActive: 'border-orange-300 bg-orange-100 text-orange-700',
    chipSeen: 'border-orange-200 bg-orange-50 text-orange-600',
  },
  PAID: {
    dot: 'bg-emerald-500',
    badgeBg: 'bg-emerald-100',
    badgeText: 'text-emerald-600',
    chipActive: 'border-emerald-300 bg-emerald-100 text-emerald-700',
    chipSeen: 'border-emerald-200 bg-emerald-50 text-emerald-600',
  },
  READY_FOR_DELIVERY: {
    dot: 'bg-indigo-400',
    badgeBg: 'bg-indigo-100',
    badgeText: 'text-indigo-600',
    chipActive: 'border-indigo-300 bg-indigo-100 text-indigo-700',
    chipSeen: 'border-indigo-200 bg-indigo-50 text-indigo-600',
  },
  DELIVERED: {
    dot: 'bg-teal-500',
    badgeBg: 'bg-teal-100',
    badgeText: 'text-teal-600',
    chipActive: 'border-teal-300 bg-teal-100 text-teal-700',
    chipSeen: 'border-teal-200 bg-teal-50 text-teal-600',
  },
  RETURNED: {
    dot: 'bg-rose-400',
    badgeBg: 'bg-rose-100',
    badgeText: 'text-rose-600',
    chipActive: 'border-rose-300 bg-rose-100 text-rose-700',
    chipSeen: 'border-rose-200 bg-rose-50 text-rose-600',
  },
  REFUNDED: {
    dot: 'bg-pink-400',
    badgeBg: 'bg-pink-100',
    badgeText: 'text-pink-600',
    chipActive: 'border-pink-300 bg-pink-100 text-pink-700',
    chipSeen: 'border-pink-200 bg-pink-50 text-pink-600',
  },
  PRINT: {
    dot: 'bg-purple-400',
    badgeBg: 'bg-purple-100',
    badgeText: 'text-purple-600',
    chipActive: 'border-purple-300 bg-purple-100 text-purple-700',
    chipSeen: 'border-purple-200 bg-purple-50 text-purple-600',
  },
  ADD_ITEM: {
    dot: 'bg-purple-500',
    badgeBg: 'bg-purple-100',
    badgeText: 'text-purple-600',
    chipActive: 'border-purple-300 bg-purple-100 text-purple-700',
    chipSeen: 'border-purple-200 bg-purple-50 text-purple-600',
  },
};

export const FALLBACK_COLOR: StatusColors = {
  dot: 'bg-slate-400',
  badgeBg: 'bg-slate-100',
  badgeText: 'text-slate-500',
  chipActive: 'border-slate-300 bg-slate-100 text-slate-700',
  chipSeen: 'border-slate-200 bg-slate-50 text-slate-600',
};

export function humanizeState(state: string) {
  return state
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
